import { Fragment, useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { FARBEN, faerben, umschliessen, zeilenVorsatz, type Eingabe } from '../kommentartext';

const MD_HELP: [string, ReactNode][] = [
  ['**fett**', <strong>fett</strong>],
  ['*kursiv*', <em>kursiv</em>],
  ['- Punkt', '• Aufzählung (jede Zeile mit - beginnen)'],
  ['1. Punkt', '1. Nummerierte Liste'],
  ['### Überschrift', <strong>Zwischenüberschrift</strong>],
  ['[Text](https://…)', <a href="#" onClick={(e) => e.preventDefault()}>Link</a>],
  ['{rot:Text}', <span className="md"><span className="kt-rot">farbiger Text</span> – rot, orange, gruen, blau, violett, grau</span>],
  ['Leerzeile', 'neuer Absatz'],
];

/** Formatierung auf die Markierung eines Textfelds anwenden und die Markierung danach wiederherstellen. */
function anwenden(feld: RefObject<HTMLTextAreaElement | null>, setzen: (text: string) => void, aktion: (e: Eingabe) => Eingabe) {
  const ta = feld.current;
  if (!ta) return;
  const e = aktion({ text: ta.value, start: ta.selectionStart ?? ta.value.length, ende: ta.selectionEnd ?? ta.value.length });
  setzen(e.text);
  // erst nach dem Neuzeichnen steht der neue Text im Feld
  requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(e.start, e.ende); });
}

/** Strg+B = fett, Strg+I = kursiv im Textfeld; true, wenn die Taste verarbeitet wurde. */
export function mdTaste(e: KeyboardEvent<HTMLTextAreaElement>, feld: RefObject<HTMLTextAreaElement | null>, setzen: (text: string) => void): boolean {
  if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return false;
  const k = e.key.toLowerCase();
  if (k !== 'b' && k !== 'i') return false;
  e.preventDefault();
  anwenden(feld, setzen, (x) => (k === 'b' ? umschliessen(x, '**', '**') : umschliessen(x, '*', '*')));
  return true;
}

/**
 * Werkzeugleiste für Textfelder mit Formatierung (Markdown): fett, kursiv, Aufzählung, Überschrift und Farben – wirkt auf
 * die Markierung im Textfeld. Ein Klick in die Leiste nimmt dem Textfeld den Fokus nicht.
 */
export function MdLeiste({ feld, setzen, testid }: { feld: RefObject<HTMLTextAreaElement | null>; setzen: (text: string) => void; testid: string }) {
  const tun = (aktion: (e: Eingabe) => Eingabe) => () => anwenden(feld, setzen, aktion);
  return (
    <div className="md-leiste" onMouseDown={(e) => e.preventDefault()} data-testid={testid + '-leiste'}>
      <button type="button" className="btn icon small" title={'Fett (Strg+B)\nMarkierten Text fett setzen'} onClick={tun((e) => umschliessen(e, '**', '**'))} data-testid={testid + '-fett'}><b>F</b></button>
      <button type="button" className="btn icon small" title={'Kursiv (Strg+I)\nMarkierten Text kursiv setzen'} onClick={tun((e) => umschliessen(e, '*', '*'))} data-testid={testid + '-kursiv'}><i>K</i></button>
      <button type="button" className="btn icon small" title="Aufzählung: markierte Zeilen als Liste" onClick={tun((e) => zeilenVorsatz(e, '- '))} data-testid={testid + '-liste'}>•</button>
      <button type="button" className="btn icon small" title="Zwischenüberschrift für die Zeile" onClick={tun((e) => zeilenVorsatz(e, '### '))} data-testid={testid + '-ueberschrift'}><b>Ü</b></button>
      <span className="farben">
        {FARBEN.map((f) => (
          <button key={f.id} type="button" className={'farbe kt-' + f.id} title={`${f.name}\nMarkierten Text einfärben`} aria-label={`Farbe ${f.name}`}
            onClick={tun((e) => faerben(e, f.id))} data-testid={testid + '-farbe-' + f.id} />
        ))}
      </span>
    </div>
  );
}

/** Kleine Formatierungshilfe für Textfelder, die Markdown verstehen. */
export function MdHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="md-help">
      <button type="button" className="linkish" onClick={(e) => { e.preventDefault(); setOpen(!open); }} data-testid="md-help">{open ? 'Formatierungshilfe ausblenden' : 'Text formatieren?'}</button>
      {open && (
        <table>
          <thead><tr><th>Sie schreiben</th><th>Ergebnis</th></tr></thead>
          <tbody>
            {MD_HELP.map(([a, b]) => <tr key={a}><td><code>{a}</code></td><td>{b}</td></tr>)}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface FieldProps {
  label?: string;
  value: any;
  onCommit: (v: any) => void;
  parse?: (v: string) => any;
  format?: (v: any) => string;
  multiline?: boolean;
  tall?: boolean;
  placeholder?: string;
  type?: string;
  hint?: ReactNode;
  testid?: string;
  suggest?: string[];
  md?: boolean;
  disabled?: boolean;
}

/**
 * Eingabefeld mit lokalem Anzeigewert: Die Eingabe wird sofort gespeichert (parse → onCommit),
 * angezeigt wird aber exakt das Getippte (wichtig für Listen mit Komma/Zeilenumbruch).
 * Felder mit „(Markdown)“ im Label bzw. md=true bekommen eine Formatierungshilfe.
 */
export function Field({ label, value, onCommit, parse = (v) => v, format = (v) => v ?? '', multiline, tall, placeholder, type = 'text', hint, testid, suggest, md, disabled }: FieldProps) {
  const [local, setLocal] = useState(format(value));
  const [focus, setFocus] = useState(false);
  useEffect(() => {
    if (!focus) setLocal(format(value));
  }, [value, focus]);
  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLocal(e.target.value);
    onCommit(parse(e.target.value));
  };
  const isMd = md || /\(Markdown\)/.test(label || '');
  const text = (label || '').replace(/\s*\(Markdown\)/, '');
  const common = { value: local, onChange, onFocus: () => setFocus(true), onBlur: () => setFocus(false), placeholder, disabled, 'data-testid': testid };
  const feld = useRef<HTMLTextAreaElement>(null);
  const setzen = (v: string) => { setLocal(v); onCommit(parse(v)); };
  const mitLeiste = isMd && !!multiline && !disabled;
  // mit Werkzeugleiste kein <label>: Ein Klick auf einen Knopf der Leiste gälte sonst zugleich dem Textfeld
  const Tag = suggest || mitLeiste ? 'div' : 'label';
  return (
    <Tag className="field"><span>{text}</span>
      {suggest
        ? <Choice free value={value ?? ''} options={suggest.map((v) => ({ value: v, label: v }))} onChange={(v) => onCommit(parse(v ?? ''))} testid={testid} placeholder="Name wählen oder neu eintragen …" disabled={disabled} />
        : multiline ? <>{mitLeiste && <MdLeiste feld={feld} setzen={setzen} testid={testid ?? 'feld'} />}
            <textarea ref={feld} className={tall ? 'tall' : ''} {...common} onKeyDown={mitLeiste ? (e) => { mdTaste(e, feld, setzen); } : undefined} /></>
          : <input type={type} {...common} />}
      {hint && <div className="hint">{hint}</div>}
      {isMd && !disabled && <MdHelp />}
    </Tag>
  );
}

export interface Option {
  value: string | null;
  label: string;
  farbe?: string;
  gruppe?: string;
}

interface ChoiceProps {
  value: string | null | undefined;
  options: Option[];
  onChange: (v: any) => void;
  testid?: string;
  free?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Auswahl mit Filter nach IWM-Vorlage (FastStart-Stilblatt): ein Eingabefeld wie jedes andere,
 * darunter eine eigene Liste. Tippen filtert, Pfeiltasten wählen, Enter übernimmt, Esc schließt.
 * options: [{ value, label, farbe?, gruppe? }]. Mit free=true darf auch ein Wert stehen bleiben,
 * der nicht in der Liste steht (z. B. ein neuer Verantwortlicher); value ist dann der Text, onChange kommt bei jeder Eingabe.
 */
export function Choice({ value, options, onChange, testid, free = false, placeholder = 'Auswählen oder tippen zum Filtern …', disabled }: ChoiceProps) {
  const label = (v: string | null | undefined) => (free ? v ?? '' : options.find((o) => (o.value ?? '') === (v ?? ''))?.label ?? '');
  const [text, setText] = useState(label(value));
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [dran, setDran] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const current = label(value);
  useEffect(() => { if (!open) setText(current); }, [current, open]);
  const s = q.trim().toLowerCase();
  const shown = options.filter((o) => !s || o.label.toLowerCase().includes(s) || (o.gruppe || '').toLowerCase().includes(s));
  useEffect(() => {
    listRef.current?.querySelector('li.dran')?.scrollIntoView({ block: 'nearest' });
  }, [dran, open]);
  const auf = (filter = '') => {
    if (disabled) return;
    setQ(filter);
    setDran(Math.max(0, shown.findIndex((o) => (o.value ?? '') === (value ?? ''))));
    setOpen(true);
  };
  const nimm = (o: Option) => {
    setOpen(false);
    setText(o.label);
    onChange(free ? o.value : o.value || null);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return auf(q);
      setDran((d) => Math.max(0, Math.min(shown.length - 1, d + (e.key === 'ArrowDown' ? 1 : -1))));
    } else if (e.key === 'Enter') {
      if (open && shown[dran]) { e.preventDefault(); nimm(shown[dran]); }
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
    }
  };
  const onBlur = () => setTimeout(() => {
    setOpen(false);
    if (free && text !== (value ?? '')) onChange(text.trim());
  }, 140);
  let gruppe: string | null | undefined = null;
  return (
    <div className="wahl" data-testid={testid}>
      <div className="wahl-feld">
        <input ref={inputRef} type="text" role="combobox" aria-expanded={open} autoComplete="off" spellCheck={false}
          value={text} placeholder={placeholder} disabled={disabled}
          onFocus={(e) => { e.target.select(); auf(''); }}
          onMouseDown={() => { if (!open && document.activeElement === inputRef.current) auf(''); }}
          onChange={(e) => { setText(e.target.value); setQ(e.target.value); setDran(0); if (!disabled) setOpen(true); if (free) onChange(e.target.value); }}
          onKeyDown={onKey} onBlur={onBlur} />
        <span className="wahl-pfeil" onMouseDown={(e) => {
          e.preventDefault();
          if (open) setOpen(false);
          // schon fokussiert (z. B. nach dem Zuklappen oder einer Auswahl): kein neues onFocus – direkt aufklappen
          else if (document.activeElement === inputRef.current) auf('');
          else if (!disabled) inputRef.current?.focus();
        }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
        </span>
      </div>
      {open && (
        <ul className="wahl-liste" role="listbox" ref={listRef}>
          {shown.length === 0 && <li className="wahl-leer" role="presentation">{free ? 'Kein Treffer – die Eingabe wird übernommen.' : 'Kein Treffer.'}</li>}
          {shown.map((o, i) => {
            const head = o.gruppe && o.gruppe !== gruppe ? <li className="wahl-gruppe" role="presentation">{o.gruppe}</li> : null;
            gruppe = o.gruppe;
            return (
              <Fragment key={(o.value ?? '') + '|' + i}>
                {head}
                <li role="option" data-value={o.value ?? ''} aria-selected={(o.value ?? '') === (value ?? '')} className={i === dran ? 'dran' : ''}
                  onMouseDown={(e) => { e.preventDefault(); nimm(o); }} onMouseMove={() => i !== dran && setDran(i)}>
                  {o.farbe && <i className="sw" style={{ background: o.farbe }} />}{o.label}
                </li>
              </Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface SelectProps {
  label: string;
  value: string | null | undefined;
  options: Option[];
  onChange: (v: any) => void;
  testid?: string;
  disabled?: boolean;
}

export function Select({ label, value, options, onChange, testid, disabled }: SelectProps) {
  return (
    <div className="field"><span>{label}</span>
      <Choice value={value} options={options} onChange={onChange} testid={testid} disabled={disabled} />
    </div>
  );
}

export function Check({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean | undefined; onChange: (v: boolean) => void; disabled?: boolean }) {
  return <label className="check"><input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />{label}</label>;
}

export const listParse = (sep: string) => (v: string) => v.split(sep).map((s) => s.trim()).filter(Boolean);

export const quellenFormat = (q: any[] | undefined) => (q || []).map((x) => x.dokument + (x.datum ? ' | ' + x.datum : '')).join('\n');
/** Quellen aus dem Textfeld; mit `alt` behalten Zeilen mit unverändertem Dokumentnamen ihren Link auf einen Anhang (anhangId). */
export const quellenParse = (v: string, alt?: any[]) =>
  v.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    const [dok, dat] = l.split('|').map((s) => s.trim());
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(dat || '') ? dat : /^(\d{2})\.(\d{2})\.(\d{4})$/.test(dat || '') ? dat.replace(/^(\d{2})\.(\d{2})\.(\d{4})$/, '$3-$2-$1') : '';
    const anhangId = (alt || []).find((q) => q?.anhangId && q.dokument === dok)?.anhangId;
    return anhangId ? { dokument: dok, datum: iso, anhangId } : { dokument: dok, datum: iso };
  });

/** Freitextfeld für Filter: tippt lokal, gibt den Text nach kurzer Pause weiter (Adresse wird nicht bei jedem Zeichen neu geschrieben). */
export function TextFilter({ wert, onChange, testid, platzhalter = 'Text in Titel oder Beschreibung' }: {
  wert: string; onChange: (text: string) => void; testid: string; platzhalter?: string;
}) {
  const [text, setText] = useState(wert);
  useEffect(() => setText(wert), [wert]);
  useEffect(() => {
    if (text === wert) return;
    const t = setTimeout(() => onChange(text), 250);
    return () => clearTimeout(t);
  }, [text]);
  return (
    <input type="search" className="filter-text" value={text} placeholder={platzhalter} aria-label="Filtern nach Text"
      onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape' && text) { e.stopPropagation(); setText(''); } }}
      data-testid={testid} />
  );
}
